import { Collapse, Modal, Tabs, Tag, Typography } from "antd";
import { FC, ReactNode, useRef, useState } from "react";

const { Text, Paragraph } = Typography;

interface IOperatorGuideEntry {
  name: string;
  syntax: string;
  description: string;
  example: string | string[];
  notes?: string[];
}

const RESPONSE_OPERATORS: IOperatorGuideEntry[] = [
  {
    name: "AND",
    syntax: "AND(response_variable_1, response_variable_2, ...)",
    description:
      "Passes only when every response you list has been answered. Insert response variables with the # shortcut.",
    example: "AND(Minor_waivfail_Granted, Minor_waivfail_Age_18_or_older)",
    notes: [
      "You can nest another operator's result inside AND, e.g. AND(OR(a, b), c).",
    ],
  },
  {
    name: "OR",
    syntax: "OR(response_variable_1, response_variable_2, ...)",
    description:
      "Passes when at least one of the listed responses has been answered.",
    example: "OR(Minor_waivfail_Granted, Minor_waivfail_Denied)",
    notes: ["Nesting works the same way as it does in AND."],
  },
  {
    name: "EQUAL",
    syntax: 'EQUAL(response_variable, "value")',
    description:
      "Passes when the answer to a response matches the value you give it. Not case-sensitive — \"Judicial\" and \"judicial\" are treated the same.",
    example: 'EQUAL(Minor_waivfail_Type, "Judicial")',
  },
  {
    name: "NOTEQUAL",
    syntax: 'NOTEQUAL(response_variable, "value")',
    description:
      "Passes when the answer to a response does NOT match the value you give it.",
    example: 'NOTEQUAL(Minor_waivfail_Type, "Judicial")',
  },
  {
    name: "COMPARE",
    syntax: 'COMPARE(response_variable, operator, "value"[, type])',
    description:
      "Compares a response's answer against a value using =, !=, <, >, >=, or <=. Use this instead of EQUAL/NOTEQUAL whenever you need a greater-than or less-than check.",
    example: [
      'COMPARE(Minor_waivfail_Age, >=, "18")',
      'COMPARE(Event_date, >=, "08/21/2026", DATE)',
    ],
    notes: [
      "By default the comparison is numeric, so value should be a plain number — non-numeric answers simply won't match.",
      'Add DATE as a fourth parameter to compare dates instead, e.g. COMPARE(Event_date, >=, "08/21/2026", DATE). Saved answers are read as YYYY-MM-DD, then MM/DD/YYYY, then a full written-out date, in that order — an answer in none of these forms won\'t match.',
    ],
  },
];

const QUESTION_OPERATORS: IOperatorGuideEntry[] = [
  {
    name: "QAND",
    syntax: "QAND(question_variable)",
    description:
      "Passes only if every option under a question has been answered — a shortcut for writing AND across a whole question at once. Insert the question variable with the @ shortcut.",
    example: "QAND(Minor_waivfail)",
  },
  {
    name: "QOR",
    syntax: "QOR(question_variable)",
    description:
      "Passes if at least one option under a question has been answered — a shortcut for writing OR across a whole question at once.",
    example: "QOR(Minor_waivfail)",
  },
  {
    name: "QHASANSWER",
    syntax: "QHASANSWER(question_variable)",
    description:
      "Passes if the question has been answered at all, no matter which option was chosen.",
    example: "QHASANSWER(Minor_waivfail)",
  },
  {
    name: "QHASNOTANSWER",
    syntax: "QHASNOTANSWER(question_variable)",
    description: "Passes if the question was left unanswered.",
    example: "QHASNOTANSWER(Minor_waivfail)",
  },
];

const OTHER_OPERATORS: IOperatorGuideEntry[] = [
  {
    name: "IMPORT",
    syntax: "IMPORT(rank_name)",
    description:
      "Reuses another rank's already-computed pass/fail result as if it were a variable. Useful for building tiers, where a higher rank must meet everything a lower rank requires, plus more. Insert the rank name with the $ shortcut.",
    example: "AND(IMPORT(Gold), QOR(Additional_criteria))",
    notes: [
      "A rank can only import an equal-or-lower rank — importing a higher rank isn't allowed.",
      "The rank being imported needs its own valid, saved equation for this to work correctly.",
    ],
  },
];

const SHORTCUTS: { trigger: string; label: string; description: string }[] = [
  {
    trigger: "@",
    label: "Question variables",
    description:
      "Insert a question variable, for QAND/QOR/QHASANSWER/QHASNOTANSWER.",
  },
  {
    trigger: "#",
    label: "Response variables",
    description: "Insert a response variable, for AND/OR/EQUAL/NOTEQUAL/COMPARE.",
  },
  {
    trigger: ":",
    label: "Operators",
    description: "Insert any operator name from this guide, pre-filled with ().",
  },
  {
    trigger: "$",
    label: "Ranks",
    description:
      "Insert another rank's name, for IMPORT — only shows ranks that are valid to import (excludes the current tab and any higher rank).",
  },
];

const MODAL_BODY_MAX_HEIGHT = 420;

// Matches a shortcut trigger character immediately followed by the word "shortcut" in an
// operator's description (e.g. "the # shortcut") — the trigger becomes a clickable link that
// jumps to and expands that trigger's entry in the Other tab's Typing shortcuts list.
const SHORTCUT_MENTION_PATTERN = /([$@#:]) shortcut/g;

const renderDescriptionWithShortcutLinks = (
  description: string,
  onShortcutClick: (trigger: string) => void,
): ReactNode[] => {
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  SHORTCUT_MENTION_PATTERN.lastIndex = 0;
  while ((match = SHORTCUT_MENTION_PATTERN.exec(description))) {
    const trigger = match[1];
    if (match.index > lastIndex) {
      parts.push(description.slice(lastIndex, match.index));
    }
    parts.push(
      <Text
        key={`shortcut-link-${key++}`}
        className="operatorGuideShortcutLink"
        onClick={() => onShortcutClick(trigger)}
      >
        {trigger}
      </Text>,
    );
    parts.push(" shortcut");
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < description.length) {
    parts.push(description.slice(lastIndex));
  }
  return parts;
};

const ScrollableTabBody: FC<{ children: ReactNode }> = ({ children }) => (
  <div style={{ maxHeight: MODAL_BODY_MAX_HEIGHT, overflowY: "auto" }}>
    {children}
  </div>
);

const OperatorList: FC<{
  entries: IOperatorGuideEntry[];
  onShortcutClick: (trigger: string) => void;
}> = ({ entries, onShortcutClick }) => (
  <Collapse
    items={entries.map((entry) => ({
      key: entry.name,
      label: (
        <span>
          <Tag className="operatorTag">{entry.name}</Tag>
          <Text code className="operatorGuideSyntax">
            {entry.syntax}
          </Text>
        </span>
      ),
      children: (
        <>
          <Paragraph style={{ marginBottom: 8 }}>
            {renderDescriptionWithShortcutLinks(
              entry.description,
              onShortcutClick,
            )}
          </Paragraph>
          <div>
            <Text strong>
              {Array.isArray(entry.example) ? "Examples" : "Example"}
            </Text>
            {(Array.isArray(entry.example)
              ? entry.example
              : [entry.example]
            ).map((example, index) => (
              <Text code className="operatorGuideExample" key={index}>
                {example}
              </Text>
            ))}
          </div>
          {entry.notes && (
            <ul className="operatorGuideNotes">
              {entry.notes.map((note, index) => (
                <li key={index}>
                  <Text type="secondary">{note}</Text>
                </li>
              ))}
            </ul>
          )}
        </>
      ),
    }))}
  />
);

const OperatorGuideModal: FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => {
  const [activeTabKey, setActiveTabKey] = useState("response");
  const [openShortcutKeys, setOpenShortcutKeys] = useState<string[]>([]);
  const shortcutRefs = useRef<Record<string, HTMLElement | null>>({});

  const navigateToShortcut = (trigger: string) => {
    setActiveTabKey("other");
    setOpenShortcutKeys((prev) =>
      prev.includes(trigger) ? prev : [...prev, trigger],
    );
    setTimeout(() => {
      shortcutRefs.current[trigger]
        ?.closest(".ant-collapse-item")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  return (
    <Modal
      className="operatorGuideModal"
      title="Ranking Equation — Operator Guide"
      open={open}
      onCancel={onClose}
      onOk={onClose}
      cancelButtonProps={{ style: { display: "none" } }}
      okText="Close"
      width={720}
    >
      <div className="operatorGuideIntro">
        <Paragraph type="secondary">
          Every equation is built from these operators. Nest one inside
          another (e.g. <Text code>AND(OR(a, b), c)</Text>) to combine logic.
        </Paragraph>
      </div>
      <div className="operatorGuideIntro">
        <Paragraph type="secondary">
          Wrap a comparison <Text strong>value</Text> in double quotes — e.g.{" "}
          <Text code>EQUAL(var, "Judicial")</Text>,{" "}
          <Text code>COMPARE(var, {">"}, "18")</Text>. Quotes go around the
          value only — never around a response/question variable, a rank
          name, or an operator symbol.
        </Paragraph>
      </div>

      <Tabs
        activeKey={activeTabKey}
        onChange={setActiveTabKey}
        items={[
          {
            key: "response",
            label: "Response",
            forceRender: true,
            children: (
              <ScrollableTabBody>
                <OperatorList
                  entries={RESPONSE_OPERATORS}
                  onShortcutClick={navigateToShortcut}
                />
              </ScrollableTabBody>
            ),
          },
          {
            key: "question",
            label: "Question",
            forceRender: true,
            children: (
              <ScrollableTabBody>
                <OperatorList
                  entries={QUESTION_OPERATORS}
                  onShortcutClick={navigateToShortcut}
                />
              </ScrollableTabBody>
            ),
          },
          {
            key: "other",
            label: "Other",
            forceRender: true,
            children: (
              <ScrollableTabBody>
                <OperatorList
                  entries={OTHER_OPERATORS}
                  onShortcutClick={navigateToShortcut}
                />
                <Text strong className="operatorGuideShortcutsHeading">
                  Typing shortcuts
                </Text>
                <Collapse
                  activeKey={openShortcutKeys}
                  onChange={(keys) =>
                    setOpenShortcutKeys(
                      Array.isArray(keys) ? keys : [keys].filter(Boolean),
                    )
                  }
                  items={SHORTCUTS.map((shortcut) => ({
                    key: shortcut.trigger,
                    label: (
                      <span
                        ref={(el) => {
                          shortcutRefs.current[shortcut.trigger] = el;
                        }}
                      >
                        <Tag className="shortcutTag">{shortcut.trigger}</Tag>
                        <Text strong style={{ marginLeft: 5 }}>
                          {shortcut.label}
                        </Text>
                      </span>
                    ),
                    children: (
                      <Text type="secondary">{shortcut.description}</Text>
                    ),
                  }))}
                />
              </ScrollableTabBody>
            ),
          },
        ]}
      />
    </Modal>
  );
};

export default OperatorGuideModal;
